import _cloneDeep from 'lodash/cloneDeep'
import _orderBy from "lodash/orderBy"
import _sortBy from 'lodash/sortBy'
import latinize from 'latinize'
import { ACTION_PLAN_EFFICIENCY_TYPES, PROBLEMS_CLOSE } from 'frontend/constants/problems'
import {
  addIndicator,
  addIndicatorAssessment,
  deleteIndicator,
  getCauseCategories,
  getEfficiencyIndicators,
  getProblemCategories,
  getProblemEscalateTechniques,
  getProblemsById,
  getProblemsByParameters,
  updateIndicator,
  updateIndicatorAssessment,
} from 'frontend/communications/apiClient'
import { PROMISE_STATUS } from 'frontcore/constants/promises'
import { sortAlphabetically } from 'frontcore/utils/helpers/formatters'


const UNDEFINED = -1

function orderIndicators (indicators) {
  indicators.forEach(indicator => {
    indicator.assessments = orderAssessments(indicator.assessments)
  })
  return _orderBy(indicators, [
    assessment => new Date(assessment.closed_date),
    'order',
  ], ['asc', 'desc'])
}

function orderAssessments (assessments) {
  const isArrayOfObjects = assessments.some(item => Object.keys(item).length)
  if (!isArrayOfObjects) return assessments
  return _sortBy(assessments, [
    assessment => new Date(assessment.date),
    'order',
  ])
}

const stateObj = {
  causeCategories: [],
  efficiencyIndicators: [],
  isInDetailsPage: false,
  presentation: [],
  problem: null,
  problemCategories: [],
  problemCategoriesAlreadyFetched: false,
  problems: [],
  problemEscalateTechniques: [],
  problemEscalateTechniquesPromise: null,
  problemEscalateTechniquesLoadingStatus: PROMISE_STATUS.notStarted,
}

const moduleGetters = {
  getProblem (state) {
    return state.problem
  },
  getProblemById: (state) => (id) => {
    return state.problems.find(problem => problem.id == id)
  },
  getIsInDetailsPage (state) {
    return state.isInDetailsPage
  },
  isProblemClosed (state) {
    return state.problem ? state.problem.status === PROBLEMS_CLOSE : false
  },
  efficiencyIndicators (state) {
    return state.efficiencyIndicators
  },
  getCauseCategoriesById: (state) => (id) => {
    return state.causeCategories.find(category => category.id === id)
  },
  getProblemCategories (state) {
    return state.problemCategories
  },
  getActiveProblemCategories (state) {
    return state.problemCategories.filter(category => {
      return category.enabled
    }).sort(
      sortAlphabetically('name')
    )
  },
  getPresentation (state) {
    return state.presentation
  },
  getEnabledProblemEscalateTechniques (state, getters) {
    return _sortBy(
      getters.getProblemEscalateTechniques,
      problemEscalateTechnique => latinize(problemEscalateTechnique.name.toLowerCase())
    ).filter(problemEscalateTechnique => {
      return problemEscalateTechnique.enabled
    })
  },
  getProblemEscalateTechniques (state) {
    if (!state.problemEscalateTechniquesPromise) {
      console.error("Fetch problemEscalateTechniques first:\n", (new Error()).stack)
    }
    return _sortBy(
      state.problemEscalateTechniques,
      problemEscalateTechnique => latinize(problemEscalateTechnique.name.toLowerCase())
    )
  },
  problemEscalateTechniquesAreLoaded (state) {
    return state.problemEscalateTechniquesLoadingStatus === PROMISE_STATUS.complete
  },
  getIndicatorAssessments: (state) => (indicatorId) => {
    const indicatorIndex = state.efficiencyIndicators.findIndex(indicatorEntry => indicatorEntry.id === indicatorId)
    if (indicatorIndex === UNDEFINED) { return [] }
    return orderAssessments(state.efficiencyIndicators[indicatorIndex].assessments)
  },
}

const mutations = {
  setProblem (state, value) {
    state.problem = value
    state.efficiencyIndicators = value.efficiency_indicators || []
  },
  setProblems (state, value) {
    state.problems = value
  },
  setRecommendedProblems (state, value) {
    state.recommendedProblems = value
  },
  setIsInDetailsPage (state, value) {
    state.isInDetailsPage = value
  },
  setCauseCategories (state, value) {
    state.causeCategories = value
  },
  setProblemCategories (state, value) {
    state.problemCategories = value
  },
  setPresentation (state, value) {
    state.presentation = value
  },
  setProblemEscalateTechniques (state, value) {
    state.problemEscalateTechniques = value
  },
  setProblemEscalateTechniquesLoadingStatus (state, status) {
    state.problemEscalateTechniquesLoadingStatus = status
  },
  setEfficiencyIndicators (state, value) {
    state.efficiencyIndicators = orderIndicators(value)
  },
  updateEfficiencyIndicator (state, value) {
    const indicators = _cloneDeep(state.efficiencyIndicators)
    const index = indicators.findIndex(indicator => indicator.id === value.id)
    if (index === UNDEFINED) {
      indicators.push(value)
    } else {
      indicators.splice(index, 1, value)
    }
    state.efficiencyIndicators = orderIndicators(indicators)
  },
  updateEfficiencyAssessment (state, value) {
    const indicators = _cloneDeep(state.efficiencyIndicators)
    const indicatorIndex = indicators.findIndex(indicatorEntry => indicatorEntry.id === value.efficiency_indicator)
    if (indicatorIndex === UNDEFINED) { return }
    const indicator = indicators[indicatorIndex]
    const index = indicator.assessments.findIndex(ind => ind.id === value.id)
    if (index === UNDEFINED) {
      indicator.assessments.push(value)
    } else {
      indicator.assessments.splice(index, 1, value)
    }
    indicator.assessments = orderAssessments(indicator.assessments)
    state.efficiencyIndicators.splice(indicatorIndex, 1, indicator)
  },
  deleteEfficiencyIndicator (state, value) {
    const index = state.efficiencyIndicators.findIndex(indicator => indicator.id === value.id)
    if (index === UNDEFINED) { return }
    state.efficiencyIndicators.splice(index, 1)
  },
  updateProblemCategory (state, value) {
    const categoryIndex = state.problemCategories.findIndex(category => category.id === value.id)
    if (categoryIndex !== -1) {
      state.problemCategories.splice(categoryIndex, 1, value)
    }
  },
  updateCauseCategory (state, value) {
    const categoryIndex = state.causeCategories.findIndex(category => category.id === value.id)
    if (categoryIndex !== -1) {
      state.causeCategories.splice(categoryIndex, 1, value)
    }
  },
}

const actions = {
  async fetchProblemById ({ commit }, id) {
    const problem = await getProblemsById(id, null)
    commit('setProblem', problem)
    return problem
  },
  async fetchEfficiencyIndicators ({ commit }, problemID) {
    const response = await getEfficiencyIndicators(problemID)
    commit('setEfficiencyIndicators', response)
    return response
  },
  async createEfficiencyAssessment ({ commit }, assessment) {
    const response = await addIndicatorAssessment(assessment, assessment.efficiency_indicator)
    commit('updateEfficiencyAssessment', response)
    return response
  },
  async createEfficiencyIndicator ({ commit }, indicator) {
    const response = await addIndicator(indicator.problem, indicator)
    commit('updateEfficiencyIndicator', response)
    return response
  },
  async updateEfficiencyIndicator ({ commit, getters }, indicator) {
    const response = await updateIndicator(indicator.problem, indicator)
    if (response.problem === getters.getProblem.id) {
      if (getters.getPlanEfficiencyType !== ACTION_PLAN_EFFICIENCY_TYPES.efficiency_kpi) {
        response.assessments = response.assessments.map(
          id => indicator.assessments.find(assessment => assessment.id === id)
        )
      }
      commit('updateEfficiencyIndicator', response)
      return response
    }
    return null
  },
  async updateEfficiencyAssessment ({ commit }, assessment) {
    const response = await updateIndicatorAssessment(assessment, assessment.efficiency_indicator)
    if (response.id === assessment.id) {
      commit('updateEfficiencyAssessment', response)
      return response
    }
    return null
  },
  async deleteEfficiencyIndicator ({ commit, getters }, indicator) {
    const response = await deleteIndicator(indicator.problem, indicator)
    if (indicator.problem === getters.getProblem.id) {
      commit('deleteEfficiencyIndicator', indicator)
      return response
    }
    return null
  },
  async fetchCauseCategories ({ commit }) {
    const response = await getCauseCategories()
    commit('setCauseCategories', response)
    return response
  },
  fetchProblemCategories ({ commit, state }, forceUpdate) {
    if (state.problemCategoriesAlreadyFetched && !forceUpdate) {
      return state.problemCategories
    }
    return getProblemCategories().then(response => {
      const orderedCategories = _orderBy(response, 'name')
      commit('setProblemCategories', orderedCategories)
      state.problemCategoriesAlreadyFetched = true
      return orderedCategories
    })
  },
  async fetchProblems ({ commit }, payload) {
    const response = await getProblemsByParameters({ format: 'minimal',  ...payload})
    commit('setProblems', response)
    return response
  },
  fetchProblemEscalateTechniques ({ commit, state }, force=false) {
    if (state.problemEscalateTechniquesPromise == null || force) {
      state.problemEscalateTechniquesPromise = getProblemEscalateTechniques()
      commit('setProblemEscalateTechniquesLoadingStatus', PROMISE_STATUS.pending)
    }
    return state.problemEscalateTechniquesPromise.then((problemEscalateTechniques) => {
      (force || state.problemEscalateTechniques.length === 0) ? commit('setProblemEscalateTechniques', problemEscalateTechniques) : Promise.resolve()
    })
      .catch(e => {
        state.problemEscalateTechniquesPromise = null
        throw e
      })
      .finally(() => {
        commit('setProblemEscalateTechniquesLoadingStatus', PROMISE_STATUS.complete)
      })
  },
}

export default {
  state: stateObj,
  getters: moduleGetters,
  mutations,
  actions,
}
